import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "wouter";
import { AlertTriangle, Loader2, MapPinned, Pencil, Plus, RefreshCw, Trash2 } from "lucide-react";
import { apiRequest, getApiPath } from "@/lib/queryClient";
import { useAdminBasePath } from "@/lib/adminPath";
import {
  DELIVERY_ZONES_PATH,
  ORDER_WINDOW_CONFIG_KEY,
  WEEKDAYS,
  ZONE_PICK_MATCH,
  ZONE_PICK_NONE,
  daysLabel,
  orgDeliveryDays,
  refreshSummary,
  scheduleAllowsZones,
  splitList,
  stackedDays,
  zonePickBody,
  zonesByFirstDay,
  type AddressRefreshResult,
  type DeliveryZoneRow,
  type DeliveryZonesResponse,
  type ZoneScheduleConfig,
} from "@/lib/deliveryZones";
import { useToast } from "@/hooks/use-toast";
import { parseApiError } from "@/components/pin-setup-code-step";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

// Settings → Fulfillment: delivery zones (BRIEF-customer-types-zones-picksheet.md §2d,
// §5 rows 10–13). A zone is a run: its delivery days and the towns or postal code areas
// it serves. Zones stack — a customer is in every zone that lists their address and can
// choose any of those zones' days — so a schedule is entered the way it is printed: a
// zone per day, each listing that day's towns. They are shown by first delivery day.
// Zones can be set up while "Delivery days" (in Fulfillment Options, saved with that card)
// says "Same for everyone" — they apply only once it says "Vary by zone".

const UNZONED = "none";

type ZoneDraft = { id: number | null; name: string; days: number; prefixes: string; towns: string };

function ZoneDialog({
  draft,
  orgDays,
  onClose,
  onSave,
  saving,
}: {
  draft: ZoneDraft | null;
  orgDays: number;
  onClose: () => void;
  onSave: (draft: ZoneDraft) => void;
  saving: boolean;
}) {
  const [value, setValue] = useState<ZoneDraft | null>(draft);
  // Reset when a different zone (or a new one) is opened.
  const [openedFor, setOpenedFor] = useState<ZoneDraft | null>(draft);
  if (draft !== openedFor) {
    setOpenedFor(draft);
    setValue(draft);
  }
  if (!value) return null;
  const days = value.days & orgDays;
  const canSave = value.name.trim() !== "" && days !== 0 && !saving;

  return (
    <Dialog open={!!draft} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{value.id === null ? "New delivery zone" : `Edit ${draft?.name}`}</DialogTitle>
          <DialogDescription>
            A customer is in every zone that lists their town or postal code area, and can choose any of those zones'
            days. A zone's days are picked from your delivery days.
          </DialogDescription>
        </DialogHeader>
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (canSave) onSave({ ...value, days });
          }}
        >
          <div className="space-y-1.5">
            <Label htmlFor="zone-name">Name</Label>
            <Input
              id="zone-name"
              data-testid="input-zone-name"
              value={value.name}
              maxLength={80}
              placeholder="e.g., Monday, or Hanover run"
              onChange={(e) => setValue({ ...value, name: e.target.value })}
            />
          </div>
          <div className="space-y-1.5">
            <Label>Delivery days</Label>
            <div className="flex flex-wrap gap-1.5">
              {[1, 2, 3, 4, 5, 6, 0].map((bit) => {
                const offered = (orgDays & (1 << bit)) !== 0;
                const on = offered && (value.days & (1 << bit)) !== 0;
                return (
                  <Button
                    key={bit}
                    type="button"
                    size="sm"
                    variant={on ? "default" : "outline"}
                    disabled={!offered}
                    title={offered ? undefined : "Turn this on in your delivery schedule first"}
                    aria-pressed={on}
                    data-testid={`button-zone-day-${bit}`}
                    className={on ? "bg-cn-brand-blue hover:bg-cn-brand-blue-hover" : undefined}
                    onClick={() => setValue({ ...value, days: value.days ^ (1 << bit) })}
                  >
                    {WEEKDAYS[bit].short}
                  </Button>
                );
              })}
            </div>
            {days === 0 && <p className="text-sm text-red-600">Pick at least one day.</p>}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="zone-prefixes">Postal code areas</Label>
            <Input
              id="zone-prefixes"
              data-testid="input-zone-prefixes"
              value={value.prefixes}
              placeholder="e.g., N0G, N4N"
              onChange={(e) => setValue({ ...value, prefixes: e.target.value })}
            />
            <p className="text-xs text-gray-500">The first three characters of a postal code, separated by commas.</p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="zone-towns">Towns</Label>
            <Input
              id="zone-towns"
              data-testid="input-zone-towns"
              value={value.towns}
              placeholder="e.g., Hanover, Walkerton"
              onChange={(e) => setValue({ ...value, towns: e.target.value })}
            />
            <p className="text-xs text-gray-500">
              Separated by commas. List a town in every zone that delivers there.
            </p>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={!canSave} data-testid="button-save-zone" className="bg-cn-brand-blue hover:bg-cn-brand-blue-hover">
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Save zone
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function DeliveryZonesCard() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const adminBase = useAdminBasePath();
  const zonesKey = [getApiPath(DELIVERY_ZONES_PATH)];
  // Which customers the list shows: null = Unzoned, else a zone's.
  const [listZone, setListZone] = useState<number | null>(null);
  const { data, isLoading, isError, refetch, isRefetching } = useQuery<DeliveryZonesResponse>({
    queryKey: [...zonesKey, { zone: listZone }],
    queryFn: async () =>
      (await apiRequest("GET", listZone === null ? DELIVERY_ZONES_PATH : `${DELIVERY_ZONES_PATH}?zone=${listZone}`)).json(),
    placeholderData: (previous) => previous,
  });
  const { data: config } = useQuery<ZoneScheduleConfig>({ queryKey: [...ORDER_WINDOW_CONFIG_KEY] });

  const [draft, setDraft] = useState<ZoneDraft | null>(null);
  const [deleting, setDeleting] = useState<DeliveryZoneRow | null>(null);

  const orgDays = orgDeliveryDays(config);
  const byZone = config?.deliveryDaysByZone === true;
  const zonesAllowed = scheduleAllowsZones(config) && config?.id !== 0;

  const refreshZones = () => {
    queryClient.invalidateQueries({ queryKey: zonesKey });
    // Account pages show the zone.
    queryClient.invalidateQueries({ queryKey: ["/api/accounts"] });
  };
  const fail = (title: string) => (error: unknown) =>
    toast({ title, description: parseApiError(error).message ?? "Please try again.", variant: "destructive" });

  const saveZone = useMutation({
    mutationFn: async (zone: ZoneDraft) => {
      const body = {
        name: zone.name.trim(),
        deliveryDays: zone.days,
        postalPrefixes: splitList(zone.prefixes),
        cities: splitList(zone.towns),
      };
      if (zone.id === null) {
        return apiRequest("POST", DELIVERY_ZONES_PATH, { ...body, sortOrder: data?.zones.length ?? 0 });
      }
      return apiRequest("PATCH", `${DELIVERY_ZONES_PATH}/${zone.id}`, body);
    },
    onSuccess: (_res, zone) => {
      setDraft(null);
      refreshZones();
      toast({ title: zone.id === null ? "Zone added" : "Zone saved", description: "Customers are matched to it by their address." });
    },
    onError: fail("Couldn't save the zone"),
  });

  const deleteZone = useMutation({
    mutationFn: (zone: DeliveryZoneRow) => apiRequest("DELETE", `${DELIVERY_ZONES_PATH}/${zone.id}`),
    onSuccess: (_res, zone) => {
      setDeleting(null);
      refreshZones();
      toast({ title: `${zone.name} deleted`, description: "Its customers keep the days of any other zone that lists them." });
    },
    onError: fail("Couldn't delete the zone"),
  });

  const assign = useMutation({
    mutationFn: ({ accountId, pick }: { accountId: number; pick: string }) =>
      apiRequest("PUT", `/api/accounts/${accountId}/delivery-zone`, zonePickBody(pick)),
    onSuccess: refreshZones,
    onError: fail("Couldn't set the zone"),
  });

  const refreshAddresses = useMutation({
    mutationFn: async (): Promise<AddressRefreshResult> => (await apiRequest("POST", "/api/quickbooks/sync/refresh-addresses")).json(),
    onSuccess: (result) => {
      refreshZones();
      toast({ title: "Addresses refreshed from QuickBooks", description: refreshSummary(result) });
    },
    onError: fail("Couldn't refresh addresses"),
  });

  const zones = zonesByFirstDay(data?.zones ?? [], orgDays);
  const list = data?.list;
  const listedZone = listZone === null ? null : zones.find((zone) => zone.id === listZone) ?? null;
  const unzonedTotal = listZone === null ? list?.total : undefined;
  // The zone being listed was deleted: back to Unzoned.
  useEffect(() => {
    if (listZone !== null && data && !data.zones.some((zone) => zone.id === listZone)) setListZone(null);
  }, [data, listZone]);

  return (
    <Card data-testid="card-delivery-zones">
      <CardHeader className="border-b border-gray-100 bg-gray-50/50">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <MapPinned className="h-5 w-5" /> Delivery Zones
            </CardTitle>
            <CardDescription className="mt-[5px] mb-[5px]">
              List the towns or postal code areas each run serves, and the days it runs. A customer on more than one
              run can choose any of those days.
            </CardDescription>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={refreshAddresses.isPending}
            onClick={() => refreshAddresses.mutate()}
            data-testid="button-refresh-addresses"
          >
            {refreshAddresses.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
            Refresh addresses from QuickBooks
          </Button>
        </div>
      </CardHeader>
      <CardContent className="pt-6 space-y-6">
        {isLoading ? (
          <div className="flex justify-center py-4">
            <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
          </div>
        ) : isError || !data ? (
          <div className="flex items-center justify-between gap-4">
            <p className="text-sm text-red-600">Couldn't load the delivery zones.</p>
            <Button type="button" variant="outline" size="sm" disabled={isRefetching} onClick={() => refetch()}>
              {isRefetching && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Try again
            </Button>
          </div>
        ) : (
          <>
            {!byZone && zones.length > 0 && (
              <p className="text-sm text-amber-700" data-testid="text-zones-not-applied">
                Zones are not applied yet: every customer sees all your delivery days until "Delivery days by customer"
                above is set to "Vary by zone"{zonesAllowed ? "" : " (it needs a Daily or Custom schedule)"}.
              </p>
            )}

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label>Zones</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setDraft({ id: null, name: "", days: orgDays, prefixes: "", towns: "" })}
                  data-testid="button-add-zone"
                >
                  <Plus className="h-4 w-4 mr-1" /> Add zone
                </Button>
              </div>
              {zones.length === 0 ? (
                <p className="text-sm text-gray-500">No zones yet. Every customer can choose any of your delivery days.</p>
              ) : (
                <ul className="divide-y divide-gray-100 rounded-md border border-gray-200">
                  {zones.map((zone) => {
                    const effective = zone.deliveryDays & orgDays;
                    return (
                      <li key={zone.id} className="flex items-start justify-between gap-3 p-3" data-testid={`row-zone-${zone.id}`}>
                        <div className="min-w-0 space-y-0.5">
                          <p className="font-medium">
                            {zone.name}{" "}
                            <button
                              type="button"
                              className="text-sm font-normal text-cn-brand-blue hover:underline"
                              data-testid={`button-list-zone-${zone.id}`}
                              onClick={() => {
                                setListZone(zone.id);
                                document.querySelector('[data-testid="section-zone-customers"]')?.scrollIntoView({ behavior: "smooth", block: "start" });
                              }}
                            >
                              · {zone.accountCount} customer{zone.accountCount === 1 ? "" : "s"}
                            </button>
                          </p>
                          <p className="text-sm text-gray-700">{daysLabel(effective)}</p>
                          <p className="text-xs text-gray-500 break-words">
                            {[zone.postalPrefixes.join(", "), zone.cities.join(", ")].filter(Boolean).join(" · ") || "No postal areas or towns — put customers in it by hand"}
                          </p>
                          {effective === 0 && (
                            <p className="flex items-center gap-1 text-xs text-red-600">
                              <AlertTriangle className="h-3.5 w-3.5" /> None of this zone's days are delivery days any more, so a customer in no other zone can't choose a delivery day.
                            </p>
                          )}
                        </div>
                        <div className="flex shrink-0 gap-1">
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            aria-label={`Edit ${zone.name}`}
                            onClick={() =>
                              setDraft({
                                id: zone.id,
                                name: zone.name,
                                days: zone.deliveryDays,
                                prefixes: zone.postalPrefixes.join(", "),
                                towns: zone.cities.join(", "),
                              })
                            }
                          >
                            <Pencil className="h-4 w-4 text-gray-500" />
                          </Button>
                          <Button type="button" variant="ghost" size="icon" aria-label={`Delete ${zone.name}`} onClick={() => setDeleting(zone)}>
                            <Trash2 className="h-4 w-4 text-gray-500" />
                          </Button>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            <div className="space-y-2" data-testid="section-zone-customers">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <Label>
                  {listedZone ? `Customers in ${listedZone.name}` : "Unzoned customers"}
                  {list ? ` (${list.total})` : ""}
                </Label>
                <Select value={listZone === null ? UNZONED : String(listZone)} onValueChange={(v) => setListZone(v === UNZONED ? null : Number(v))}>
                  <SelectTrigger className="w-full sm:w-64" aria-label="Show customers" data-testid="select-zone-list">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={UNZONED}>Unzoned{unzonedTotal !== undefined ? ` (${unzonedTotal})` : ""}</SelectItem>
                    {zones.map((zone) => (
                      <SelectItem key={zone.id} value={String(zone.id)}>
                        {zone.name} ({zone.accountCount})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <p className="text-sm text-gray-500">
                {listedZone
                  ? "Customers whose town or postal code area this zone lists, or who were put in it by hand. One who is in other zones too gets all of their days."
                  : "Customers no zone lists. They get all your delivery days. Add their town or postal code area to a zone, or pick one zone for them here."}
              </p>
              {list && list.accounts.length > 0 ? (
                <ul className="divide-y divide-gray-100 rounded-md border border-gray-200 max-h-96 overflow-y-auto" data-testid="list-zone-customers">
                  {list.accounts.map((account) => {
                    // In the order the zones are shown.
                    const mine = zones.filter((zone) => account.zoneIds.includes(zone.id));
                    const days = stackedDays(mine, orgDays);
                    // By hand: one zone, or none. Otherwise the address decides.
                    const pick = account.pinned ? (account.zoneIds.length > 0 ? String(account.zoneIds[0]) : ZONE_PICK_NONE) : ZONE_PICK_MATCH;
                    return (
                      <li key={account.id} className="flex flex-col gap-2 p-3 sm:flex-row sm:items-center sm:justify-between" data-testid={`row-zone-customer-${account.id}`}>
                        <div className="min-w-0">
                          <Link href={`${adminBase}/accounts/${account.id}`} className="font-medium text-cn-brand-blue hover:underline">
                            {account.name}
                          </Link>
                          <p className="text-xs text-gray-500" title={mine.length > 0 ? `In ${mine.map((zone) => zone.name).join(", ")}` : undefined}>
                            {[account.town, account.postalCode].filter(Boolean).join(" ") || "No address"}
                            {mine.length > 0 && ` · ${daysLabel(days)}`}
                            {account.pinned && (mine.length > 0 ? " · set by hand" : " · left unzoned by hand")}
                          </p>
                        </div>
                        <Select
                          value={pick}
                          disabled={zones.length === 0 || assign.isPending}
                          onValueChange={(value) => {
                            if (value !== pick) assign.mutate({ accountId: account.id, pick: value });
                          }}
                        >
                          <SelectTrigger className="w-full sm:w-52" aria-label={`Zones for ${account.name}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value={ZONE_PICK_MATCH}>Match by address</SelectItem>
                            {zones.map((zone) => (
                              <SelectItem key={zone.id} value={String(zone.id)}>
                                Only {zone.name}
                              </SelectItem>
                            ))}
                            <SelectItem value={ZONE_PICK_NONE}>No zone</SelectItem>
                          </SelectContent>
                        </Select>
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <p className="text-sm text-gray-500">{listedZone ? "No customers in this zone yet." : "Every customer is in a zone."}</p>
              )}
              {list && list.total > list.accounts.length && (
                <p className="text-xs text-gray-500">
                  Showing the first {list.accounts.length} of {list.total}.
                </p>
              )}
              {data.capped && (
                <p className="text-xs text-amber-700">These counts cover your first 5,000 customers by name.</p>
              )}
            </div>
          </>
        )}
      </CardContent>

      <ZoneDialog
        draft={draft}
        orgDays={orgDays}
        onClose={() => setDraft(null)}
        onSave={(zone) => saveZone.mutate(zone)}
        saving={saveZone.isPending}
      />

      <AlertDialog open={!!deleting} onOpenChange={(open) => !open && setDeleting(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {deleting?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              Its {deleting?.accountCount ?? 0} customer{deleting?.accountCount === 1 ? "" : "s"} will lose its days. One that
              another zone lists keeps that zone's days; one that no other zone lists gets all your delivery days.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                if (deleting) deleteZone.mutate(deleting);
              }}
              disabled={deleteZone.isPending}
            >
              {deleteZone.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Delete zone
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
